/**
 * useInitUserRole bootstraps the Redux user slice with Cognito details
 * and extended profile data. It mirrors the original InitializeUserRole
 * component logic to keep behavior identical while making it reusable.
 */
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { logger } from '@/lib/logger';
import { clearPermissions } from '@/redux/permissionsSlice';
import { clearUser, setUserProfile, setUserRole, setUsername } from '../../redux/userSlice';
import { normalizeUserRole } from '../../utils/roles';
import { applyTestDelay } from '../../utils/testLoadingDelay';

export interface UseInitUserRoleResult {
  /**
   * True while the initial Cognito session lookup and profile fetch are
   * in flight. Consumers can use this to hold the route tree behind a
   * loading state so we never render a "no role yet" frame on first load.
   */
  isInitializing: boolean;
}

/**
 * Initializes the Redux user state with data pulled from Cognito and the API.
 * Mirrors the previous InitializeUserRole component logic in App.tsx and now
 * also reports whether the bootstrap is still in progress.
 * Returns isInitializing to track loading state.
 */
export const useInitUserRole = (): UseInitUserRoleResult => {
  const dispatch = useDispatch();
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized) {
      return;
    }

    let isMounted = true;
    const fetchUserData = async () => {
      try {
        await applyTestDelay('authDelay');
        const session = await fetchAuthSession();

        if (!isMounted) return;

        if (!session?.tokens?.idToken) {
          // No valid session — clear any stale persisted role
          dispatch(clearPermissions());
          dispatch(clearUser());
          if (isMounted) {
            setIsInitializing(false);
            setHasInitialized(true);
          }
          return;
        }

        const idToken = session.tokens.idToken;
        const rawJwtRole = idToken.payload['custom:role'] as string;
        const userRole = normalizeUserRole(rawJwtRole) ?? rawJwtRole;
        const username = idToken.payload['cognito:username'] as string;
        dispatch(setUserRole(userRole));
        dispatch(setUsername(username));

        if (session?.tokens?.accessToken && username) {
          try {
            const response = await axios.get(`users/users/by-cognito-id/${username}`, {
              headers: {
                Authorization: `Bearer ${session.tokens.accessToken}`,
              },
            });

            if (!isMounted) return;

            const profile = response.data?.Profile;
            const profileRole = normalizeUserRole(profile?.role) ?? userRole;
            const userId = profile?.user_profile_id || response.data?.id || response.data?.uuid;
            dispatch(
              setUserProfile({
                username,
                role: profileRole,
                first_name: profile?.first_name || null,
                last_name: profile?.last_name || null,
                user_id: userId || username,
              })
            );
          } catch (error) {
            logger.error('Error fetching profile data', error);
            // Continue with basic role/username even if profile fetch fails
          }
        }
      } catch (error) {
        logger.error('Error fetching user data', error);
        if (isMounted) {
          dispatch(clearPermissions());
          dispatch(clearUser());
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false);
          setHasInitialized(true);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [dispatch, hasInitialized]);

  return { isInitializing };
};

export const InitializeUserRole = () => {
  useInitUserRole();
  return null;
};
